import React, { useCallback, useEffect, useMemo } from 'react';

import currencyFormatter from 'currency-formatter';
import { Controller, useForm, useWatch } from 'react-hook-form';

import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Input, Select } from '~/components/Common/Forms/fields';

import { REVERSAL_REASONS } from '../constants';

import CashPaymentsInfo from './CashPaymentsInfo';
import OrderSummaryCard from './OrderSummaryCard';
import type { CreditReversalFormFields, CreditReversalFormProps } from './types';
import { calculateCashReversed, calculateForfeitAmount } from './utils';

const OTHER_REASON_VALUE = 'other';
const RETAIN_CREDIT_REASON_VALUE = 'refund_90_retain_10_credit';

export default function CreditReversalForm({ currentCreditBalance, order, onSubmit }: CreditReversalFormProps) {
  const { control, handleSubmit, setValue } = useForm<CreditReversalFormFields>({
    defaultValues: {
      caseTicketNumber: '',
      reason: '',
      otherReason: '',
      isPaymentMethodValid: null,
      orderId: order.orderId,
      bookingId: order.bookingId ?? '',
      currency: order.currency,
      cashReversedAmount: 0,
      submissionNotes: '',
    },
    mode: 'all',
  });

  const reason = useWatch({ control, name: 'reason' });
  const creditDeductionAmount = useWatch({ control, name: 'creditDeductionAmount' });

  const cashReversed = useMemo(() => {
    const amount = Number(creditDeductionAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return 0;
    }
    return calculateCashReversed(reason, amount);
  }, [reason, creditDeductionAmount]);

  useEffect(() => {
    setValue('cashReversedAmount', cashReversed);
  }, [cashReversed, setValue]);

  const handleFormSubmit = useCallback(
    (fields: CreditReversalFormFields) => {
      onSubmit(fields);
    },
    [onSubmit],
  );

  const forfeitAmount = useMemo(() => {
    const amount = Number(creditDeductionAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return 0;
    }
    return calculateForfeitAmount(reason, amount);
  }, [reason, creditDeductionAmount]);

  const cashReversedFormatted = currencyFormatter.format(cashReversed, {
    code: order.currency,
    precision: 2,
  });

  const formatCurrency = useCallback(
    (amount: number) => currencyFormatter.format(amount, { code: order.currency, precision: 2 }),
    [order.currency],
  );
  const formattedCurrentCreditBalance = useMemo(
    () => `${formatCurrency(currentCreditBalance)} ${order.currency}`,
    [currentCreditBalance, formatCurrency, order.currency],
  );

  return (
    <Stack
      spacing={2}
      component="form"
      id="credit-reversal-form"
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{ px: 4 }}
    >
      <OrderSummaryCard order={order} />

      <Input
        control={control}
        name="caseTicketNumber"
        muiLabel="Case/Ticket Number"
        fullWidth
        required
        rules={{ required: 'Case/Ticket Number is required' }}
      />

      <Input control={control} name="bookingId" muiLabel="Booking ID" disabled />

      <Select
        control={control}
        name="reason"
        muiLabel="Reason for Credit Reversal"
        rules={{ required: 'Reason is required' }}
      >
        {REVERSAL_REASONS.map((r) => (
          <MenuItem key={r.value} value={r.value}>
            {r.label}
          </MenuItem>
        ))}
      </Select>

      {reason === OTHER_REASON_VALUE && (
        <Input
          control={control}
          name="otherReason"
          muiLabel="Please specify the reason"
          fullWidth
          required
          rules={{ required: 'Please specify the reason' }}
        />
      )}

      <CashPaymentsInfo order={order} />

      <Controller
        control={control}
        name="isPaymentMethodValid"
        rules={{ validate: (v) => v !== null || 'Please confirm whether the payment method is valid' }}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error}>
            <FormLabel required>Confirmed with customer, the payment method is still valid?</FormLabel>
            <RadioGroup
              row
              {...field}
              value={field.value === null ? '' : String(field.value)}
              onChange={(e) => field.onChange(e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />

      <Input
        control={control}
        name="currency"
        muiLabel="Currency"
        disabled
        helperText="Used for both credit deduction and cash reversed"
      />

      {reason === RETAIN_CREDIT_REASON_VALUE ? (
        <Alert severity="warning">Enter the already-calculated 90% value as Credit Deduction</Alert>
      ) : null}

      {forfeitAmount > 0 && cashReversed > 0 && (
        <Alert severity="info">
          Out of {formatCurrency(Number(creditDeductionAmount))} credit deducted, {formatCurrency(forfeitAmount)} will
          be forfeited and {formatCurrency(cashReversed)} will be reversed as cash to customer.
        </Alert>
      )}

      <Controller
        control={control}
        name="creditDeductionAmount"
        rules={{
          required: 'Credit deduction amount is required',
          min: { value: 0.01, message: 'Amount must be greater than 0' },
          validate: {
            maxReversal: (value) =>
              value <= order.maxReversal ||
              `Amount cannot exceed maximum reversal of ${formatCurrency(order.maxReversal)}`,
            availableCredits: (value) =>
              value <= currentCreditBalance ||
              `Amount cannot exceed available credits of ${formatCurrency(currentCreditBalance)}`,
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth error={!!error} variant="standard">
            <TextField
              label="Credit Deduction"
              type="number"
              required
              inputProps={{ step: 0.01, min: 0, max: Math.min(order.maxReversal, currentCreditBalance) }}
              value={field.value}
              onChange={(e) => field.onChange(e.target.value === '' ? 0 : Number(e.target.value))}
              onBlur={field.onBlur}
              error={!!error}
            />
            <FormHelperText>{error?.message}</FormHelperText>
            <FormHelperText>
              <Typography variant="body2" component="span" sx={{ textDecoration: 'underline' }}>
                Available Credits:{' '}
                <Typography variant="body2" component="span" fontWeight="bold">
                  {formattedCurrentCreditBalance}
                </Typography>
              </Typography>
            </FormHelperText>
          </FormControl>
        )}
      />

      <Input
        control={control}
        name="cashReversedAmount"
        muiLabel="Cash Reversed to Customer"
        disabled
        value={cashReversedFormatted}
      />

      <Input control={control} name="submissionNotes" muiLabel="Submission Notes" fullWidth rows={3} />
    </Stack>
  );
}
